import {
  SystemPermissions,
  TERMINAL_FULFILLMENT_STATUSES,
  type MarketplaceFulfillmentStatus,
} from "@nexus/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Button,
  Checkbox,
  DateRangePicker,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Tabs,
  TabsList,
  Toggle,
  cn,
  type DateRange,
} from "@nexus/ui";
import { toast } from "sonner";

import { AdminSearchInput } from "@/features/admin/components/layout/AdminSearchInput";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useTenantSwitch } from "@/features/auth/hooks/useTenantSwitch";
import {
  useIamPermission,
  useIsSuperTenant,
  usePermissions,
} from "@/features/auth/hooks/usePermissions";
import {
  ADMIN_ORDERS_CELL_ACTIONS_CLASS,
  ADMIN_ORDERS_CELL_DUE_CLASS,
  ADMIN_ORDERS_CELL_PARTNER_CLASS,
  ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
  ADMIN_ORDERS_CELL_SENT_CLASS,
  ADMIN_ORDERS_CELL_STATUS_CLASS,
  ADMIN_ORDERS_CELL_TYPE_CLASS,
  ADMIN_ORDERS_GRID_CLASS,
} from "@/features/marketplace/admin/components/admin-orders-grid";
import { AdminOrdersOverview } from "@/features/marketplace/admin/components/AdminOrdersOverview";
import type { AdminOrdersOverviewAction } from "@/features/marketplace/admin/components/admin-orders-overview";
import { AdminOrdersMultiSelect } from "@/features/marketplace/admin/components/AdminOrdersMultiSelect";
import { AdminOrderTableRow } from "@/features/marketplace/admin/components/AdminOrderTableRow";
import {
  SendToPartnerDialog,
  isMissingTargetRatios,
  type SendToPartnerSubmission,
} from "@/features/marketplace/admin/components/SendToPartnerDialog";
import { useCanEditMarketplaceOrderFulfillment } from "@/features/marketplace/admin/hooks/useCanEditMarketplaceOrderFulfillment";
import { downloadOrderScriptAsset } from "@/features/marketplace/api/marketplace.api";
import { OrdersDetailPanel } from "@/features/marketplace/components/orders-list/OrdersDetailPanel";
import { OrdersBulkBar } from "@/features/marketplace/components/orders-list/OrdersBulkBar";
import { OrderLineTypeFilterMenu } from "@/features/marketplace/components/orders-list/OrderLineTypeFilterMenu";
import { SortableColumnHeader } from "@/features/marketplace/components/orders-list/SortableColumnHeader";
import {
  STATUS_FILTER_OPTION_ITEM_CLASS,
  StatusOptionContent,
} from "@/features/marketplace/components/orders-list/StatusOptionContent";
import { VendorIdentity } from "@/features/marketplace/components/orders-list/VendorIdentity";
import { useBulkStatusChange } from "@/features/marketplace/components/orders-list/useBulkStatusChange";
import { OrgLogoMark } from "@/features/identity-hub/components/dashboard/OrgLogoMark";
import type { OrgPickerItem } from "@/features/identity-hub/components/dashboard/OrgPickerCombobox";
import { OrgPickerOptionContent } from "@/features/identity-hub/components/dashboard/org-picker-shared";
import { tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";
import {
  loadPanelWidth,
  resolvePanelTarget,
  type OrdersPanelTarget,
} from "@/features/marketplace/lib/orders-detail-panel";
import {
  formatYmdLong,
  formatYmdShort,
  localTodayYmd,
  parseYmdToLocalDate,
  toLocalYmd,
} from "@/features/marketplace/lib/order-deadline";
import { applyTenantPickerLogos } from "@/features/marketplace/lib/apply-tenant-picker-logos";
import {
  requiresVideoSelectionForRevision,
  resolveRevisionDeliverableId,
} from "@/features/marketplace/lib/revision-deliverable";
import {
  toOrderLineTypeFilter,
  type OrderLineTypeFilterOption,
} from "@/features/marketplace/lib/order-line-kind";
import { describeOrderScript } from "@/features/marketplace/lib/order-script-display";
import { readCreateOrderUnavailable } from "@/features/marketplace/lib/order-errors";
import { exportAdminOrdersXlsx } from "@/features/marketplace/lib/orders-export";
import {
  NATIVE_FULFILLMENT_OPTIONS,
  bulkFulfillmentOptionsForOpsAdmin,
  planBulkStatusChange,
} from "@/features/marketplace/lib/orders-list-model";
import { useStatusFilterExpansion } from "@/features/marketplace/hooks/useStatusFilterExpansion";
import {
  keepStatusFilterMatches,
  opsTrackerLines,
  opsTrackerStatusFilterExpansion,
} from "@/features/marketplace/lib/status-filter-match";
import type {
  AdminOrderRow,
  AdminOrdersDeadlineBucket,
  AdminOrdersFilters,
  AdminOrdersFulfillmentGroup,
  AdminOrdersSortBy,
  AdminOrdersSortDirection,
  MarketplaceOrder,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { tenantPickerQueryOptions } from "@/lib/queries";
import { filterSelectableTenants } from "@/lib/queries/page-state.queries";
import {
  adminOrdersQueryOptions,
  adminOrderVendorsQueryOptions,
  adminOrdersSummaryQueryOptions,
  adminVendorsQueryOptions,
  useDuplicateOrderScript,
  useSendOrderScriptToPartner,
  useUpdateOrderDueDate,
  useUpdateOrderScriptFulfillment,
} from "@/lib/queries";

const PAGE_SIZE = 20;
const PERSONA_OPPORTUNITIES_PAGE = SystemPermissions.PERSONA_OPPORTUNITIES_VIEW_PAGE;
const SEARCH_DEBOUNCE_MS = 300;

type OrdersTrackerTab = "all" | AdminOrdersFulfillmentGroup;

// Returning the same reference when nothing changed lets React skip the
// re-render; a fresh object every time loops when `data` is not stable.
function patchAdminOrderRow(
  current: AdminOrderRow,
  updated: MarketplaceOrder,
): AdminOrderRow {
  const next: AdminOrderRow = {
    ...current,
    ...keepStatusFilterMatches(current, updated),
    tenant: current.tenant,
  };
  const isUnchanged = (Object.keys(next) as (keyof AdminOrderRow)[]).every(
    (key) => next[key] === current[key],
  );
  return isUnchanged ? current : next;
}

function mergeLoadedAdminOrders(
  current: AdminOrderRow[],
  pageOrders: AdminOrderRow[],
  offset: number,
): AdminOrderRow[] {
  const updates = new Map(pageOrders.map((order) => [order.id, order]));
  if (current.length === 0) return pageOrders;
  const merged = current.map((order) => {
    const next = updates.get(order.id);
    return next ? patchAdminOrderRow(order, next) : order;
  });
  if (offset !== 0) {
    for (const order of pageOrders) {
      if (!merged.some((row) => row.id === order.id)) {
        merged.push(order);
      }
    }
  }
  const hasChanged =
    merged.length !== current.length ||
    merged.some((row, index) => row !== current[index]);
  return hasChanged ? merged : current;
}

function toggleId(current: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(current);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

function resolveLinkedAdminOrder(
  orders: readonly AdminOrderRow[],
  requestedId: string,
): AdminOrderRow | undefined {
  const needle = requestedId.trim().toLowerCase();
  if (!needle) return undefined;
  const exact = orders.find((order) => order.id.toLowerCase() === needle);
  if (exact) return exact;
  const suffixMatches = orders.filter((order) =>
    order.id.toLowerCase().endsWith(needle),
  );
  return suffixMatches.length === 1 ? suffixMatches[0] : undefined;
}

export function OrdersDashboardPage({
  initialOrderId = "",
}: {
  initialOrderId?: string;
}) {
  const { t, i18n } = useTranslation();
  const [clientTenantIds, setClientTenantIds] = useState<string[]>([]);
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  const [orderId, setOrderId] = useState(initialOrderId);
  const [isResolvingInitialOrder, setIsResolvingInitialOrder] = useState(
    () => Boolean(initialOrderId.trim()),
  );
  const [statuses, setStatuses] = useState<MarketplaceFulfillmentStatus[]>([]);
  const [lineType, setLineType] = useState<OrderLineTypeFilterOption>("all");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [deadlineBucket, setDeadlineBucket] =
    useState<AdminOrdersDeadlineBucket>();
  const [ordersTab, setOrdersTab] = useState<OrdersTrackerTab>("active");
  const [overviewOwnsGroup, setOverviewOwnsGroup] = useState(false);
  const fulfillmentGroup: AdminOrdersFulfillmentGroup | undefined =
    ordersTab === "all" ? undefined : ordersTab;

  useEffect(() => {
    setOrderId(initialOrderId);
    setOverviewOwnsGroup(false);
    setOrdersTab("active");
    setIsResolvingInitialOrder(Boolean(initialOrderId.trim()));
  }, [initialOrderId]);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [deliveredFrom, setDeliveredFrom] = useState("");
  const [deliveredTo, setDeliveredTo] = useState("");
  const [sortBy, setSortBy] = useState<AdminOrdersSortBy>("sent");
  const [sortDirection, setSortDirection] =
    useState<AdminOrdersSortDirection>("desc");
  const [offset, setOffset] = useState(0);
  const [loadedOrders, setLoadedOrders] = useState<AdminOrderRow[]>([]);
  const [exportPending, setExportPending] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [panelTarget, setPanelTarget] = useState<OrdersPanelTarget | null>(
    null,
  );
  const [panelVideoKey, setPanelVideoKey] = useState<string | null>(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [expandedScriptVideoIds, setExpandedScriptVideoIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  // Orders opened in full from a "hidden by filter" note; reset with the filters.
  const [revealedOrderIds, setRevealedOrderIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const scrollContainerRef = useRef<Element | null>(null);
  const paginationSentinelRef = useRef<HTMLDivElement | null>(null);
  const setPageRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node?.parentElement ?? null;
  }, []);
  const permissions = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const authTenants = useAuth((state) => state.tenants);
  const canEditOps = useCanEditMarketplaceOrderFulfillment();
  const updateDueDate = useUpdateOrderDueDate();
  const canViewActivity = useIamPermission(
    permissions.MARKETPLACE_ORDERS_VIEW_PAGE,
  );
  const canAddComment = useIamPermission(
    permissions.MARKETPLACE_ADMIN_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT,
  );
  const canDuplicateScriptPermission = useIamPermission(
    permissions.MARKETPLACE_ADMIN_DUPLICATE_ORDER_SCRIPT,
  );
  const canDuplicateScripts = isSuperTenant && canDuplicateScriptPermission;
  const canSendToPartnerPermission = useIamPermission(
    permissions.MARKETPLACE_ADMIN_SEND_ORDER_SCRIPT_TO_PARTNER,
  );
  const canSendToPartner = isSuperTenant && canSendToPartnerPermission;
  const [sendTarget, setSendTarget] = useState<{
    order: AdminOrderRow;
    script: OrderScriptRow;
  } | null>(null);
  const sendToPartner = useSendOrderScriptToPartner();
  // The retry key is derived from the partner and note, so a reopened dialog must start from the unresolved submission.
  const [unresolvedSends, setUnresolvedSends] = useState<
    Record<string, SendToPartnerSubmission>
  >({});
  // Filter changes clear the panel, so a toast's View order opens it once the order loads.
  const [pendingPanelOrderId, setPendingPanelOrderId] = useState<string | null>(null);
  const duplicateScript = useDuplicateOrderScript();
  const duplicateInFlight = useRef(new Set<string>());
  const switchTenant = useTenantSwitch();
  const navigate = useNavigate();
  const updateScript = useUpdateOrderScriptFulfillment();
  const bulkStatus = useBulkStatusChange({ admin: true });
  const shouldBypassFulfillmentGroup =
    isResolvingInitialOrder && orderId.trim() === initialOrderId.trim();

  const filters = useMemo<AdminOrdersFilters>(
    () => ({
      clientTenantIds: clientTenantIds.length > 0 ? clientTenantIds : undefined,
      vendorIds: vendorIds.length > 0 ? vendorIds : undefined,
      orderId: orderId.trim() || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      dueFrom: dueFrom || undefined,
      dueTo: dueTo || undefined,
      attentionOnly: attentionOnly || undefined,
      deadlineBucket,
      fulfillmentGroup: shouldBypassFulfillmentGroup
        ? undefined
        : fulfillmentGroup,
      lineType: toOrderLineTypeFilter(lineType),
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
      deliveredFrom: deliveredFrom || undefined,
      deliveredTo: deliveredTo || undefined,
      sortBy,
      sortDirection,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      clientTenantIds,
      attentionOnly,
      createdFrom,
      createdTo,
      deadlineBucket,
      deliveredFrom,
      deliveredTo,
      dueFrom,
      dueTo,
      fulfillmentGroup,
      lineType,
      shouldBypassFulfillmentGroup,
      offset,
      orderId,
      sortBy,
      sortDirection,
      statuses,
      vendorIds,
    ],
  );

  useEffect(() => {
    setOffset(0);
    setLoadedOrders([]);
    setSelectedOrderIds(new Set());
    setExpandedOrderIds(new Set());
    setExpandedScriptVideoIds(new Set());
    setRevealedOrderIds(new Set());
    setPanelTarget(null);
  }, [
    clientTenantIds,
    attentionOnly,
    createdFrom,
    createdTo,
    deadlineBucket,
    deliveredFrom,
    deliveredTo,
    dueFrom,
    dueTo,
    fulfillmentGroup,
    lineType,
    orderId,
    sortBy,
    sortDirection,
    statuses,
    vendorIds,
  ]);

  const { data: tenantPage } = useQuery(
    tenantPickerQueryOptions({ enabled: isSuperTenant }),
  );
  const tenants = useMemo<OrgPickerItem[]>(
    () =>
      isSuperTenant
        ? (tenantPage?.items ?? [])
        : filterSelectableTenants(authTenants),
    [authTenants, isSuperTenant, tenantPage],
  );
  const displayOrders = useMemo(
    () => applyTenantPickerLogos(loadedOrders, tenants),
    [loadedOrders, tenants],
  );
  const statusFilter = useMemo(() => new Set(statuses), [statuses]);
  const statusFilterActive = statusFilter.size > 0;
  const statusFilterExpansion = useMemo(
    () => opsTrackerStatusFilterExpansion(displayOrders, statusFilterActive),
    [displayOrders, statusFilterActive],
  );
  const statusFilterKey = statuses.join(",");
  const toggleStoredOrderExpanded = useCallback((orderId: string) => {
    setExpandedOrderIds((current) => toggleId(current, orderId));
  }, []);
  const toggleStoredScriptVideosExpanded = useCallback((scriptId: string) => {
    setExpandedScriptVideoIds((current) => toggleId(current, scriptId));
  }, []);
  const orderExpansion = useStatusFilterExpansion({
    statusFilterKey,
    stored: expandedOrderIds,
    autoExpanded: statusFilterExpansion.orderIds,
    onToggleStored: toggleStoredOrderExpanded,
  });
  const scriptVideoExpansion = useStatusFilterExpansion({
    statusFilterKey,
    stored: expandedScriptVideoIds,
    autoExpanded: statusFilterExpansion.scriptIds,
    onToggleStored: toggleStoredScriptVideosExpanded,
  });
  const visibleExpandedOrderIds = orderExpansion.expandedIds;
  const visibleExpandedScriptVideoIds = scriptVideoExpansion.expandedIds;
  const { data: catalogVendors = [] } = useQuery({
    ...adminVendorsQueryOptions(),
    enabled: isSuperTenant,
  });
  const { data: scopedVendors = [] } = useQuery({
    ...adminOrderVendorsQueryOptions(),
    enabled: !isSuperTenant,
  });
  const vendors = isSuperTenant ? catalogVendors : scopedVendors;
  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isFetching: isOverviewFetching,
    isError: isOverviewError,
    refetch: refetchOverview,
  } = useQuery(
    adminOrdersSummaryQueryOptions({
      clientTenantIds: clientTenantIds.length > 0 ? clientTenantIds : undefined,
    }),
  );
  const { data, isError, isLoading, isFetching } = useQuery(
    adminOrdersQueryOptions(filters),
  );

  useEffect(() => {
    if (!shouldBypassFulfillmentGroup) return;
    if (isError) {
      setIsResolvingInitialOrder(false);
      return;
    }
    if (!data) return;

    const linkedOrder = resolveLinkedAdminOrder(data.orders, initialOrderId);
    const nextGroup =
      linkedOrder &&
      (
        TERMINAL_FULFILLMENT_STATUSES as readonly MarketplaceFulfillmentStatus[]
      ).includes(linkedOrder.fulfillmentStatus)
        ? "history"
        : "active";
    setOrdersTab((current) => (current === nextGroup ? current : nextGroup));
    setIsResolvingInitialOrder(false);
  }, [data, initialOrderId, isError, shouldBypassFulfillmentGroup]);

  const total = data?.total ?? 0;
  const canLoadMore = offset + PAGE_SIZE < total;
  const handleLineTypeChange = useCallback(
    (next: OrderLineTypeFilterOption) => {
      setLineType(next);
      setOffset(0);
    },
    [],
  );
  const loadNextPage = useCallback(() => {
    if (isFetching || !canLoadMore) return;
    setOffset((current) => current + PAGE_SIZE);
  }, [canLoadMore, isFetching]);

  useInfiniteScroll(
    paginationSentinelRef,
    scrollContainerRef,
    loadNextPage,
    canLoadMore,
    isFetching,
    {
      enabled: !isLoading,
      recheckKey: `${loadedOrders.length}:${total}`,
    },
  );

  useEffect(() => {
    if (!data) return;
    setLoadedOrders((current) =>
      mergeLoadedAdminOrders(current, data.orders, offset),
    );
  }, [data, offset]);

  const patchLoadedOrder = useCallback((updated: MarketplaceOrder) => {
    setLoadedOrders((current) =>
      current.map((order) =>
        order.id === updated.id ? patchAdminOrderRow(order, updated) : order,
      ),
    );
  }, []);

  const selectedOrders = useMemo(
    () => loadedOrders.filter((order) => selectedOrderIds.has(order.id)),
    [loadedOrders, selectedOrderIds],
  );
  const selectedScriptIds = useMemo(
    () =>
      new Set(
        // A bulk change reaches only the lines the Status filter shows.
        selectedOrders.flatMap((order) =>
          opsTrackerLines(order, {
            active: statuses.length > 0,
            revealed: revealedOrderIds.has(order.id),
          }).map((script) => script.id),
        ),
      ),
    [revealedOrderIds, selectedOrders, statuses],
  );
  const bulkStatusOptions = useMemo(
    () =>
      bulkFulfillmentOptionsForOpsAdmin(
        planBulkStatusChange(selectedOrders, selectedScriptIds),
      ),
    [selectedOrders, selectedScriptIds],
  );
  const areAllLoadedOrdersSelected =
    loadedOrders.length > 0 && selectedOrders.length === loadedOrders.length;

  const selectPanelTarget = (target: OrdersPanelTarget | null) => {
    setPendingPanelOrderId(null);
    if (!target || target.kind === "order") setPanelVideoKey(null);
    setPanelTarget(target);
  };

  useEffect(() => {
    if (pendingPanelOrderId && orderId !== pendingPanelOrderId) {
      setPendingPanelOrderId(null);
    }
  }, [orderId, pendingPanelOrderId]);

  useEffect(() => {
    if (!pendingPanelOrderId) return;
    if (!loadedOrders.some((order) => order.id === pendingPanelOrderId)) return;
    setPanelTarget({ kind: "order", orderId: pendingPanelOrderId });
    setPendingPanelOrderId(null);
  }, [loadedOrders, pendingPanelOrderId]);

  const resolvedPanel = resolvePanelTarget(panelTarget, loadedOrders);
  const panelOrder = resolvedPanel
    ? (displayOrders.find((order) => order.id === resolvedPanel.orderId) ??
      null)
    : null;
  const { data: panelDetailPage } = useQuery({
    ...adminOrdersQueryOptions({
      orderId: panelOrder?.id,
      fulfillmentGroup,
      limit: 1,
      offset: 0,
    }),
    enabled: Boolean(panelOrder),
  });
  const displayedPanelOrder =
    panelDetailPage?.orders.find((order) => order.id === panelOrder?.id) ??
    panelOrder;
  const panelScript =
    resolvedPanel?.kind === "script" && displayedPanelOrder
      ? (displayedPanelOrder.scripts.find(
          (script) => script.id === resolvedPanel.scriptId,
        ) ?? null)
      : null;
  const pendingScriptIds = useMemo(() => {
    const ids = new Set<string>();
    if (updateScript.isPending && updateScript.variables) {
      ids.add(updateScript.variables.orderScriptId);
    }
    return ids;
  }, [updateScript.isPending, updateScript.variables]);
  const dueRange = useMemo<DateRange | undefined>(() => {
    const from = dueFrom ? parseYmdToLocalDate(dueFrom) : null;
    const to = dueTo ? parseYmdToLocalDate(dueTo) : null;
    if (!from && !to) return undefined;
    return { from: from ?? undefined, to: to ?? undefined };
  }, [dueFrom, dueTo]);

  const mutateScriptStatus = (
    order: AdminOrderRow,
    scriptId: string,
    fulfillmentStatus: MarketplaceFulfillmentStatus,
    videoKey: string | null,
  ) => {
    const script =
      order.scripts.find((row) => row.id === scriptId) ??
      (order.id === panelOrder?.id
        ? (displayedPanelOrder?.scripts.find((row) => row.id === scriptId) ??
          panelOrder?.scripts.find((row) => row.id === scriptId))
        : undefined) ??
      loadedOrders
        .find((entry) => entry.id === order.id)
        ?.scripts.find((row) => row.id === scriptId) ??
      displayOrders
        .find((entry) => entry.id === order.id)
        ?.scripts.find((row) => row.id === scriptId);
    if (!script) return;
    const deliverableId = resolveRevisionDeliverableId(script, videoKey);
    const mutationOptions = { onSuccess: patchLoadedOrder };
    if (fulfillmentStatus === "SENT_FOR_REVISION") {
      if (requiresVideoSelectionForRevision(script) && !deliverableId) {
        toast.error(
          t(
            "marketplace.ordersDashboard.revisionSelectVideo",
            "Select a video row before sending this script for revision.",
          ),
        );
        return;
      }
      updateScript.mutate(
        {
          orderId: order.id,
          orderScriptId: scriptId,
          tenantId: order.tenantId,
          fulfillmentStatus,
          revisionNote: undefined,
          deliverableId,
          admin: true,
        },
        mutationOptions,
      );
      return;
    }
    updateScript.mutate(
      {
        orderId: order.id,
        orderScriptId: scriptId,
        tenantId: order.tenantId,
        fulfillmentStatus,
        revisionNote: null,
        deliverableId: videoKey ? deliverableId : undefined,
        admin: true,
      },
      mutationOptions,
    );
  };

  const handleStatusChange = (
    scriptId: string,
    fulfillmentStatus: MarketplaceFulfillmentStatus,
  ) => {
    if (!panelOrder) return;
    mutateScriptStatus(panelOrder, scriptId, fulfillmentStatus, null);
  };

  const handleInlineVideoStatusChange = (
    orderId: string,
    scriptId: string,
    videoKey: string,
    fulfillmentStatus: MarketplaceFulfillmentStatus,
  ) => {
    const order =
      loadedOrders.find((entry) => entry.id === orderId) ??
      displayOrders.find((entry) => entry.id === orderId);
    if (!order) return;
    mutateScriptStatus(order, scriptId, fulfillmentStatus, videoKey);
  };

  const handleInlineScriptStatusChange = (
    orderId: string,
    scriptId: string,
    fulfillmentStatus: MarketplaceFulfillmentStatus,
  ) => {
    const order =
      loadedOrders.find((entry) => entry.id === orderId) ??
      displayOrders.find((entry) => entry.id === orderId);
    if (!order) return;
    mutateScriptStatus(order, scriptId, fulfillmentStatus, null);
  };

  const toggleOrderExpanded = orderExpansion.toggle;
  const toggleScriptVideosExpanded = scriptVideoExpansion.toggle;

  const openScriptPanel = (
    orderId: string,
    scriptId: string,
    videoKey?: string | null,
  ) => {
    setExpandedOrderIds((current) => new Set(current).add(orderId));
    setExpandedScriptVideoIds((current) => new Set(current).add(scriptId));
    orderExpansion.expand(orderId);
    scriptVideoExpansion.expand(scriptId);
    setPanelVideoKey(videoKey ?? null);
    setPanelTarget({ kind: "script", orderId, scriptId });
  };

  const handleExtendOrderDue = (order: AdminOrderRow, nextDueYmd: string) => {
    updateDueDate.mutate(
      {
        orderId: order.id,
        tenantId: order.tenantId,
        orderScriptId: null,
        dueDate: nextDueYmd,
      },
      {
        onSuccess: () =>
          toast.success(
            t("marketplace.orders.extend.success", {
              date: formatYmdLong(nextDueYmd, i18n.language),
            }),
          ),
      },
    );
  };

  const handleDuplicateScript = (script: OrderScriptRow) => {
    if (!displayedPanelOrder) return;
    // `disabled` only lands on the next render; a same-tick second click must stop here.
    if (duplicateInFlight.current.has(script.id)) return;
    duplicateInFlight.current.add(script.id);
    const clientTenantId = displayedPanelOrder.tenantId;
    const clientName = displayedPanelOrder.tenant.name;
    void duplicateScript
      .mutateAsync({
        orderId: displayedPanelOrder.id,
        orderScriptId: script.id,
        tenantId: clientTenantId,
      })
      .then((copy) => {
        const canOpenClientWorkspace = authTenants.some(
          (tenant) =>
            tenant.id === clientTenantId &&
            tenant.permissions.some(
              (permission) =>
                permission.type === PERSONA_OPPORTUNITIES_PAGE.type &&
                permission.route === PERSONA_OPPORTUNITIES_PAGE.route,
            ),
        );
        toast.success(
          t("marketplace.orders.list.panel.duplicateScriptSuccess", {
            client: clientName,
            defaultValue: "Script copied to {{client}}’s Your Scripts",
          }),
          canOpenClientWorkspace
            ? {
                action: {
                  label: t(
                    "marketplace.orders.list.panel.duplicateScriptOpen",
                    "Open script",
                  ),
                  onClick: () => {
                    switchTenant(clientTenantId);
                    void navigate({
                      to: "/persona-opportunities",
                      search: {
                        tab: "opportunities",
                        view: "table",
                        status: "all",
                        hubTab: "scripts",
                        setIds: copy.setId,
                      },
                    });
                  },
                },
              }
            : undefined,
        );
      })
      // The mutation's onError already shows the failure toast.
      .catch(() => undefined)
      .finally(() => {
        duplicateInFlight.current.delete(script.id);
      });
  };

  const handleSendToPartner = (submission: SendToPartnerSubmission) => {
    if (!sendTarget) return;
    const { order, script } = sendTarget;
    setUnresolvedSends((current) => ({ ...current, [script.id]: submission }));
    sendToPartner.mutate(
      {
        orderId: order.id,
        orderScriptId: script.id,
        tenantId: order.tenantId,
        payload: submission,
      },
      {
        onSuccess: (result) => {
          setUnresolvedSends((current) => {
            const next = { ...current };
            delete next[script.id];
            return next;
          });
          setSendTarget(null);
          toast.success(
            t(
              "marketplace.orders.sendToPartner.success",
              "Sent as a new order",
            ),
            result.orderId ? { action: viewOrderAction(result.orderId) } : undefined,
          );
        },
        onError: (err) => {
          const persisted = readCreateOrderUnavailable(err);
          if (!persisted) return;
          setSendTarget(null);
          toast.warning(
            t(
              "marketplace.orders.sendToPartner.handoffPending",
              "The order was created, but the partner email hasn't gone out yet. Sending again to the same partner with the same note and target ratios retries it without a second order.",
            ),
            { action: viewOrderAction(persisted.orderId) },
          );
        },
      },
    );
  };

  const viewOrderAction = (targetOrderId: string) => ({
    label: t("marketplace.orders.sendToPartner.viewOrder", "View order"),
    onClick: () => showOnlyOrder(targetOrderId),
  });

  /** Other filters and the History/Archive tab are ANDed with the ID, so they could hide the order. */
  const showOnlyOrder = (targetOrderId: string) => {
    setClientTenantIds([]);
    setVendorIds([]);
    setStatuses([]);
    setLineType("all");
    setDueFrom("");
    setDueTo("");
    setAttentionOnly(false);
    setDeadlineBucket(undefined);
    setOverviewOwnsGroup(false);
    setOrdersTab("active");
    setCreatedFrom("");
    setCreatedTo("");
    setDeliveredFrom("");
    setDeliveredTo("");
    setOrderId(targetOrderId);
    setPendingPanelOrderId(targetOrderId);
  };

  useEffect(() => {
    if (selectedOrderIds.size === 0) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedOrderIds(new Set());
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedOrderIds.size]);

  const clearSelection = () => setSelectedOrderIds(new Set());

  const handleOrderCheckedChange = (orderId: string, checked: boolean) => {
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      if (checked) next.add(orderId);
      else next.delete(orderId);
      return next;
    });
  };

  const toggleAllLoadedOrders = () => {
    setSelectedOrderIds(
      areAllLoadedOrdersSelected
        ? new Set()
        : new Set(loadedOrders.map((order) => order.id)),
    );
  };

  const handleBulkStatusChange = async (
    fulfillmentStatus: MarketplaceFulfillmentStatus,
  ) => {
    const didApply = await bulkStatus.apply(
      selectedOrders,
      selectedScriptIds,
      fulfillmentStatus,
    );
    if (didApply) clearSelection();
  };

  const handleCopyOrderIds = async () => {
    try {
      await navigator.clipboard.writeText(
        selectedOrders.map((order) => order.id).join("\n"),
      );
      toast.success(
        t("marketplace.orders.list.bulk.copiedOrderIds", {
          count: selectedOrders.length,
        }),
      );
    } catch {
      toast.error(
        t(
          "marketplace.orders.list.bulk.copyOrderIdsFailed",
          "Couldn’t copy order IDs",
        ),
      );
    }
  };

  const handleExportExcel = async () => {
    if (exportPending || loadedOrders.length === 0) return;
    setExportPending(true);
    try {
      const count = await exportAdminOrdersXlsx(loadedOrders, t);
      if (count > 0) {
        toast.success(
          t("marketplace.ordersDashboard.export.success", { count }),
        );
      }
    } catch {
      toast.error(
        t(
          "marketplace.ordersDashboard.export.error",
          "Export failed. Please try again.",
        ),
      );
    } finally {
      setExportPending(false);
    }
  };

  const clearOverviewFields = () => {
    setAttentionOnly(false);
    setDeadlineBucket(undefined);
    setCreatedFrom("");
    setCreatedTo("");
    setDeliveredFrom("");
    setDeliveredTo("");
  };

  const releaseTab = () => {
    setOverviewOwnsGroup(false);
    setOrdersTab((current) => (current === "all" ? "all" : "active"));
  };

  const selectTab = (
    tab: OrdersTrackerTab,
    options?: { fromOverview?: boolean },
  ) => {
    setOrdersTab(tab);
    setOverviewOwnsGroup(Boolean(options?.fromOverview) && tab !== "all");
  };

  const resetFilters = () => {
    setClientTenantIds([]);
    setVendorIds([]);
    setOrderId("");
    setStatuses([]);
    setLineType("all");
    setDueFrom("");
    setDueTo("");
    setAttentionOnly(false);
    setDeadlineBucket(undefined);
    releaseTab();
    setCreatedFrom("");
    setCreatedTo("");
    setDeliveredFrom("");
    setDeliveredTo("");
    setOffset(0);
    setLoadedOrders([]);
    clearSelection();
    setPanelTarget(null);
  };

  const hasActiveFilters = Boolean(
    clientTenantIds.length > 0 ||
    vendorIds.length > 0 ||
    orderId.trim() ||
    statuses.length > 0 ||
    lineType !== "all" ||
    dueFrom ||
    dueTo ||
    attentionOnly ||
    deadlineBucket ||
    (ordersTab !== "all" && ordersTab !== "active") ||
    overviewOwnsGroup ||
    createdFrom ||
    createdTo ||
    deliveredFrom ||
    deliveredTo,
  );

  const toggleStatus = (
    status: MarketplaceFulfillmentStatus,
    checked: boolean,
  ) => {
    setStatuses((current) =>
      checked
        ? [...current, status]
        : current.filter((candidate) => candidate !== status),
    );
  };

  const handleSortChange = (
    nextSortBy: AdminOrdersSortBy,
    nextSortDirection: AdminOrdersSortDirection,
  ) => {
    setSortBy(nextSortBy);
    setSortDirection(nextSortDirection);
  };

  const clearOverviewContext = () => {
    clearOverviewFields();
    releaseTab();
  };

  const handleOverviewAction = (action: AdminOrdersOverviewAction) => {
    switch (action.kind) {
      case "fulfillmentGroup": {
        const shouldClear =
          overviewOwnsGroup &&
          ordersTab === action.value &&
          !attentionOnly &&
          !deadlineBucket &&
          !createdFrom &&
          !deliveredFrom;
        clearOverviewFields();
        if (shouldClear) {
          releaseTab();
        } else {
          selectTab(action.value, { fromOverview: true });
        }
        break;
      }
      case "deliveredRecent": {
        const shouldClear =
          overviewOwnsGroup &&
          ordersTab === "history" &&
          deliveredFrom === action.from &&
          deliveredTo === action.to;
        clearOverviewFields();
        if (!shouldClear) {
          selectTab("history", { fromOverview: true });
          setDeliveredFrom(action.from);
          setDeliveredTo(action.to);
        } else {
          releaseTab();
        }
        break;
      }
      case "attention": {
        const shouldClear = attentionOnly;
        clearOverviewContext();
        setAttentionOnly(!shouldClear);
        break;
      }
      case "deadline": {
        const shouldClear = deadlineBucket === action.value;
        clearOverviewContext();
        setDueFrom("");
        setDueTo("");
        if (!shouldClear) {
          selectTab("active", { fromOverview: true });
          setDeadlineBucket(action.value);
        } else {
          releaseTab();
        }
        break;
      }
      case "status":
        setStatuses((current) =>
          current.length === 1 && current[0] === action.value
            ? []
            : [action.value],
        );
        break;
      case "vendor":
        setVendorIds((current) =>
          current.length === 1 && current[0] === action.value
            ? []
            : [action.value],
        );
        break;
      case "movement": {
        const isCreatedActive =
          action.series === "created" &&
          createdFrom === action.date &&
          createdTo === action.date;
        const isDeliveredActive =
          action.series === "delivered" &&
          deliveredFrom === action.date &&
          deliveredTo === action.date;
        clearOverviewFields();
        if (isCreatedActive || isDeliveredActive) {
          releaseTab();
          break;
        }
        if (action.series === "created") {
          releaseTab();
          setCreatedFrom(action.date);
          setCreatedTo(action.date);
        } else {
          selectTab("history", { fromOverview: true });
          setDeliveredFrom(action.date);
          setDeliveredTo(action.date);
        }
        break;
      }
    }
  };

  const overviewContext = attentionOnly
    ? t(
        "marketplace.ordersDashboard.overview.context.attention",
        "Needs attention",
      )
    : deadlineBucket && deadlineBucket !== "overdue"
      ? t(
          `marketplace.ordersDashboard.overview.deadlines.${deadlineBucket}`,
          deadlineBucket === "today"
            ? "Due today"
            : deadlineBucket === "next7Days"
              ? "Next 7 days"
              : deadlineBucket === "later"
                ? "Later"
                : "Unscheduled",
        )
      : createdFrom && createdFrom === createdTo
        ? t(
            "marketplace.ordersDashboard.overview.context.createdOn",
            "Created {{date}}",
            {
              date: formatYmdShort(createdFrom, localTodayYmd(), undefined),
            },
          )
        : deliveredFrom
          ? t(
              "marketplace.ordersDashboard.overview.context.delivered",
              "Delivered {{range}}",
              {
                range:
                  deliveredFrom === deliveredTo
                    ? formatYmdShort(deliveredFrom, localTodayYmd(), undefined)
                    : t(
                        "marketplace.ordersDashboard.overview.context.last30Days",
                        "last 30 days",
                      ),
              },
            )
          : overviewOwnsGroup && ordersTab !== "all"
            ? t(
                ordersTab === "active"
                  ? "marketplace.ordersDashboard.overview.context.openOrders"
                  : ordersTab === "history"
                    ? "marketplace.ordersDashboard.overview.context.history"
                    : "marketplace.ordersDashboard.overview.context.archive",
                ordersTab === "active"
                  ? "Open orders"
                  : ordersTab === "history"
                    ? "Order history"
                    : "Archive",
              )
            : undefined;

  return (
    <div
      ref={setPageRef}
      className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 sm:p-6"
    >
      <AdminOrdersOverview
        key={clientTenantIds.join(",") || "all-access"}
        summary={isOverviewLoading ? undefined : overviewData?.summary}
        isLoading={isOverviewLoading}
        isStale={isOverviewFetching && !isOverviewLoading}
        isError={isOverviewError}
        isGlobalScope={isSuperTenant}
        activeFilters={{
          fulfillmentGroup: overviewOwnsGroup ? fulfillmentGroup : undefined,
          attentionOnly,
          deadlineBucket,
          statuses,
          vendorIds,
          createdFrom: createdFrom || undefined,
          createdTo: createdTo || undefined,
          deliveredFrom: deliveredFrom || undefined,
          deliveredTo: deliveredTo || undefined,
        }}
        onAction={handleOverviewAction}
        onRetry={() => void refetchOverview()}
      />

      <Tabs
        value={ordersTab}
        onValueChange={(value) => {
          clearOverviewFields();
          selectTab(value === "all" ? "all" : (value as OrdersTrackerTab));
        }}
      >
        <TabsList
          variant="line"
          aria-label={t(
            "marketplace.ordersDashboard.tabs.aria",
            "Order views",
          )}
          options={[
            {
              value: "all",
              label: t("marketplace.ordersDashboard.tabs.all", "All"),
              badge: overviewData?.summary?.total,
            },
            {
              value: "active",
              label: t("marketplace.ordersDashboard.tabs.open", "Open"),
              badge: overviewData?.summary?.open,
            },
            {
              value: "history",
              label: t(
                "marketplace.ordersDashboard.tabs.history",
                "Completed / History",
              ),
              badge: overviewData?.summary?.history,
            },
            {
              value: "archive",
              label: t("marketplace.ordersDashboard.tabs.archive", "Archive"),
              badge: overviewData?.summary?.archived,
            },
          ]}
        />
      </Tabs>

      <section
        role="toolbar"
        aria-label={t(
          "marketplace.ordersDashboard.filterToolbar",
          "Filter orders",
        )}
        className="sticky top-0 z-20 -mx-1 flex flex-col gap-2 bg-background/95 px-1 py-2 backdrop-blur-sm lg:flex-row lg:items-center"
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <AdminSearchInput
            value={orderId}
            onChange={setOrderId}
            debounceMs={SEARCH_DEBOUNCE_MS}
            label={t("marketplace.ordersDashboard.orderIdFilter", "Order ID")}
            placeholder={t(
              "marketplace.ordersDashboard.orderIdPlaceholder",
              "Search order ID",
            )}
            className="w-full sm:w-64"
          />

          <AdminOrdersMultiSelect
            items={tenants}
            selectedIds={clientTenantIds}
            onChange={setClientTenantIds}
            getId={(tenant) => tenant.id}
            getLabel={(tenant) => tenant.name}
            renderItem={(tenant) => (
              <OrgPickerOptionContent org={tenant} accent={false} />
            )}
            renderSelectedItem={(tenant) => (
              <span className="flex min-w-0 items-center gap-2">
                <OrgLogoMark
                  name={tenant.name}
                  logoUrl={tenant.logoUrl}
                  accent={tenantAccentColor(tenant)}
                  isSuperTenant={tenant.type === "SUPER_TENANT"}
                  size={20}
                />
                <span className="truncate">{tenant.name}</span>
              </span>
            )}
            placeholder={t(
              "marketplace.ordersDashboard.allClients",
              "All clients",
            )}
            selectedCountLabel={(count) =>
              t("marketplace.ordersDashboard.selectedClients", {
                count,
                defaultValue_one: "{{count}} client",
                defaultValue_other: "{{count}} clients",
              })
            }
            ariaLabel={t(
              "marketplace.ordersDashboard.clientFilter",
              "Client org",
            )}
            searchPlaceholder={t(
              "marketplace.ordersDashboard.clientSearch",
              "Search clients…",
            )}
            emptyMessage={t(
              "marketplace.ordersDashboard.clientEmpty",
              "No clients match.",
            )}
            clearLabel={t(
              "marketplace.ordersDashboard.allClients",
              "All clients",
            )}
          />

          <AdminOrdersMultiSelect
            items={vendors}
            selectedIds={vendorIds}
            onChange={setVendorIds}
            getId={(vendor) => vendor.id}
            getLabel={(vendor) => vendor.name}
            renderItem={(vendor) => (
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <VendorIdentity
                  name={vendor.name}
                  logoUrl={vendor.logoUrl}
                  className="size-5"
                />
                <span className="truncate">{vendor.name}</span>
              </span>
            )}
            renderSelectedItem={(vendor) => (
              <span className="flex min-w-0 items-center gap-2">
                <VendorIdentity
                  name={vendor.name}
                  logoUrl={vendor.logoUrl}
                  className="size-5"
                />
                <span className="truncate">{vendor.name}</span>
              </span>
            )}
            placeholder={t(
              "marketplace.ordersDashboard.allPartners",
              "All partners",
            )}
            selectedCountLabel={(count) =>
              t("marketplace.ordersDashboard.selectedPartners", {
                count,
                defaultValue_one: "{{count}} partner",
                defaultValue_other: "{{count}} partners",
              })
            }
            ariaLabel={t("marketplace.ordersDashboard.vendorFilter", "Partner")}
            searchPlaceholder={t(
              "marketplace.ordersDashboard.vendorSearch",
              "Search partners…",
            )}
            emptyMessage={t(
              "marketplace.ordersDashboard.vendorEmpty",
              "No partners match.",
            )}
            contentClassName="min-w-64 bg-popover"
            clearLabel={t(
              "marketplace.ordersDashboard.allPartners",
              "All partners",
            )}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconEnd="chevron-down"
                className={cn(
                  "h-8 text-xs",
                  statuses.length > 0 &&
                    "border-primary/40 bg-primary-subtle/40 text-primary",
                )}
              >
                {t("marketplace.orders.list.statusFilter", "Status")}
                {statuses.length > 0 ? (
                  <span className="tabular-nums">{statuses.length}</span>
                ) : null}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-72">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t("marketplace.orders.list.statusFilter", "Status")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {NATIVE_FULFILLMENT_OPTIONS.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  className={STATUS_FILTER_OPTION_ITEM_CLASS}
                  checked={statuses.includes(status)}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(checked) =>
                    toggleStatus(status, checked === true)
                  }
                >
                  <StatusOptionContent status={status} />
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <OrderLineTypeFilterMenu
            value={lineType}
            onChange={handleLineTypeChange}
            renderTrigger={({ label, isActive }) => (
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconEnd="chevron-down"
                className={cn(
                  "h-8 text-xs",
                  isActive &&
                    "border-primary/40 bg-primary-subtle/40 text-primary",
                )}
              >
                {label}
              </Button>
            )}
          />

          <Toggle
            variant="outline"
            size="sm"
            pressed={deadlineBucket === "overdue"}
            aria-label={t("marketplace.orders.list.overdueOnly", "Overdue")}
            className={cn(
              "h-8 gap-1.5 px-3 text-xs",
              deadlineBucket === "overdue" &&
                "border-error/40 bg-error-subtle/60 text-error-subtle-foreground",
            )}
            onPressedChange={(pressed) => {
              clearOverviewFields();
              if (pressed) {
                setDueFrom("");
                setDueTo("");
                selectTab("active", { fromOverview: true });
                setDeadlineBucket("overdue");
              } else {
                releaseTab();
              }
            }}
          >
            <Icon name="alert-circle" size="sm" color="inherit" aria-hidden />
            {t("marketplace.orders.list.overdueOnly", "Overdue")}
          </Toggle>

          <DateRangePicker
            value={dueRange}
            onChange={(range) => {
              clearOverviewContext();
              setDueFrom(range?.from ? toLocalYmd(range.from) : "");
              setDueTo(range?.to ? toLocalYmd(range.to) : "");
            }}
            numberOfMonths={2}
            independentMonths={false}
            align="start"
            weekStartsOn={1}
            triggerClassName={cn(
              "h-8 text-xs",
              dueRange && "border-primary/40 bg-primary-subtle/40 text-primary",
            )}
            labels={{
              placeholder: t("marketplace.orders.list.dueFilter", "Due date"),
              triggerAria: t(
                "marketplace.orders.list.dueRangeAria",
                "Filter by due-date range",
              ),
              clear: t("marketplace.orders.list.clearDueRange", "Clear dates"),
              done: t("marketplace.orders.list.applyDueRange", "Apply dates"),
              apply: t("marketplace.orders.list.applyDueRange", "Apply dates"),
              from: t("marketplace.orders.list.dueFrom", "From"),
              to: t("marketplace.orders.list.dueTo", "To"),
            }}
          />

          {hasActiveFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconStart="x"
              className="h-8 text-xs text-muted-foreground"
              onClick={resetFilters}
            >
              {t("marketplace.ordersDashboard.clearFilters", "Clear filters")}
            </Button>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2 self-end text-xs tabular-nums text-muted-foreground lg:self-auto">
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart={exportPending ? "loader" : "download"}
            disabled={exportPending || loadedOrders.length === 0}
            aria-busy={exportPending || undefined}
            className={cn(
              "h-8 text-xs",
              exportPending && "[&_svg]:animate-spin",
            )}
            onClick={() => void handleExportExcel()}
          >
            {exportPending
              ? t("marketplace.ordersDashboard.export.exporting", "Exporting…")
              : t("marketplace.ordersDashboard.export.excel", "Export Excel")}
          </Button>
          {overviewContext ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconEnd="x"
              aria-label={t(
                "marketplace.ordersDashboard.overview.context.remove",
                "Remove overview filter: {{filter}}",
                { filter: overviewContext },
              )}
              className="h-8 max-w-48 truncate border-primary/30 bg-primary/5 text-xs text-primary dark:bg-primary/15"
              onClick={clearOverviewContext}
            >
              {overviewContext}
            </Button>
          ) : null}
          <span aria-live="polite">
            {t("marketplace.ordersDashboard.resultCount", {
              count: total,
              shown: loadedOrders.length,
              total,
              defaultValue_one: "{{shown}} of {{total}} order",
              defaultValue_other: "{{shown}} of {{total}} orders",
            })}
          </span>
          {isFetching ? (
            <Icon
              name="loader"
              size="xs"
              className="animate-spin"
              aria-label={t(
                "marketplace.ordersDashboard.refreshing",
                "Refreshing…",
              )}
            />
          ) : null}
        </div>
      </section>

      {selectedOrders.length > 0 ? (
        <div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-3xl shadow-lg">
          <OrdersBulkBar
            orderCount={selectedOrders.length}
            scriptCount={selectedScriptIds.size}
            statusOptions={bulkStatusOptions}
            applying={bulkStatus.applying}
            showStatusAction={canEditOps}
            onSetStatus={(status) => void handleBulkStatusChange(status)}
            onCopyOrderIds={() => void handleCopyOrderIds()}
            onClear={clearSelection}
          />
        </div>
      ) : null}

      {isLoading ? null : loadedOrders.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
          {t(
            "marketplace.ordersDashboard.empty",
            "No orders match these filters.",
          )}
        </div>
      ) : (
        <div
          role="treegrid"
          aria-label={t(
            "marketplace.ordersDashboard.tableLabel",
            "Orders across clients and production partners",
          )}
          aria-rowcount={loadedOrders.length + 1}
          aria-colcount={10}
          className="min-w-full overflow-hidden rounded-md border border-border bg-card"
        >
          <div
            role="row"
            aria-rowindex={1}
            className={cn(
              ADMIN_ORDERS_GRID_CLASS,
              "h-8 border-b border-border bg-muted/30 px-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
            )}
          >
            <span role="columnheader" className="flex items-center">
              <Checkbox
                aria-label={t(
                  "marketplace.ordersDashboard.selectAllOrders",
                  "Select all loaded orders",
                )}
                checked={
                  areAllLoadedOrdersSelected
                    ? true
                    : selectedOrders.length > 0
                      ? "indeterminate"
                      : false
                }
                disabled={bulkStatus.applying}
                onCheckedChange={toggleAllLoadedOrders}
                className="data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground"
              />
            </span>
            <span role="columnheader" aria-hidden />
            <SortableColumnHeader
              label={t(
                "marketplace.ordersDashboard.columns.client",
                "Client org",
              )}
              sortBy="client"
              activeSortBy={sortBy}
              sortDirection={sortDirection}
              onChange={handleSortChange}
            />
            <SortableColumnHeader
              label={t(
                "marketplace.ordersDashboard.columns.partner",
                "Partner",
              )}
              sortBy="partner"
              activeSortBy={sortBy}
              sortDirection={sortDirection}
              onChange={handleSortChange}
              className={ADMIN_ORDERS_CELL_PARTNER_CLASS}
            />
            <span role="columnheader" className={ADMIN_ORDERS_CELL_TYPE_CLASS}>
              {t("marketplace.ordersDashboard.columns.type", "Type")}
            </span>
            <SortableColumnHeader
              label={t(
                "marketplace.ordersDashboard.columns.scripts",
                "Scripts",
              )}
              sortBy="scripts"
              activeSortBy={sortBy}
              sortDirection={sortDirection}
              onChange={handleSortChange}
              className={ADMIN_ORDERS_CELL_SCRIPTS_CLASS}
            />
            <SortableColumnHeader
              label={t("marketplace.ordersDashboard.columns.status", "Status")}
              sortBy="status"
              activeSortBy={sortBy}
              sortDirection={sortDirection}
              onChange={handleSortChange}
              className={ADMIN_ORDERS_CELL_STATUS_CLASS}
            />
            <SortableColumnHeader
              label={t("marketplace.ordersDashboard.columns.sent", "Sent")}
              sortBy="sent"
              activeSortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirection="desc"
              onChange={handleSortChange}
              className={ADMIN_ORDERS_CELL_SENT_CLASS}
            />
            <SortableColumnHeader
              label={t("marketplace.ordersDashboard.columns.due", "Due")}
              sortBy="due"
              activeSortBy={sortBy}
              sortDirection={sortDirection}
              onChange={handleSortChange}
              className={ADMIN_ORDERS_CELL_DUE_CLASS}
            />
            <span
              role="columnheader"
              className={ADMIN_ORDERS_CELL_ACTIONS_CLASS}
            >
              {t("marketplace.ordersDashboard.columns.action", "Action")}
            </span>
          </div>
          <ul role="rowgroup" className="divide-y divide-border-subtle">
            {displayOrders.map((order, index) => (
              <AdminOrderTableRow
                key={order.id}
                order={order}
                rowIndex={index + 2}
                selected={resolvedPanel?.orderId === order.id}
                checked={selectedOrderIds.has(order.id)}
                selectionDisabled={bulkStatus.applying}
                expanded={visibleExpandedOrderIds.has(order.id)}
                expandedScriptVideoIds={visibleExpandedScriptVideoIds}
                statusFilter={statusFilter}
                revealed={revealedOrderIds.has(order.id)}
                onReveal={() =>
                  setRevealedOrderIds((current) => new Set(current).add(order.id))
                }
                panelScriptId={
                  resolvedPanel?.kind === "script" &&
                  resolvedPanel.orderId === order.id
                    ? resolvedPanel.scriptId
                    : null
                }
                panelVideoKey={
                  resolvedPanel?.kind === "script" &&
                  resolvedPanel.orderId === order.id
                    ? panelVideoKey
                    : null
                }
                canEditStatus={canEditOps}
                pendingScriptIds={pendingScriptIds}
                onCheckedChange={(checked) =>
                  handleOrderCheckedChange(order.id, checked)
                }
                onToggleExpanded={() => toggleOrderExpanded(order.id)}
                onToggleScriptVideosExpanded={toggleScriptVideosExpanded}
                onOpenScript={(scriptId, videoKey) =>
                  openScriptPanel(order.id, scriptId, videoKey)
                }
                onScriptStatusChange={(scriptId, next) =>
                  handleInlineScriptStatusChange(order.id, scriptId, next)
                }
                onVideoStatusChange={(scriptId, videoKey, next) =>
                  handleInlineVideoStatusChange(order.id, scriptId, videoKey, next)
                }
                onExtendDue={
                  canEditOps
                    ? (nextDueYmd) => handleExtendOrderDue(order, nextDueYmd)
                    : undefined
                }
                extendPending={
                  updateDueDate.isPending &&
                  updateDueDate.variables?.orderId === order.id
                }
              />
            ))}
          </ul>
        </div>
      )}

      <div
        ref={paginationSentinelRef}
        className="flex min-h-10 items-center justify-center"
      >
        {isFetching && offset > 0 ? (
          <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Icon
              name="loader"
              size="xs"
              className="animate-spin motion-reduce:animate-none"
              aria-hidden
            />
            {t("marketplace.ordersDashboard.loadingMore", "Loading more…")}
          </span>
        ) : canLoadMore ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={loadNextPage}
          >
            {t("marketplace.ordersDashboard.loadMore", "Load more")}
          </Button>
        ) : loadedOrders.length >= PAGE_SIZE ? (
          <span className="text-xs text-muted-foreground">
            {t("marketplace.ordersDashboard.endOfResults", "End of results")}
          </span>
        ) : null}
      </div>

      {resolvedPanel && displayedPanelOrder ? (
        <OrdersDetailPanel
          target={resolvedPanel}
          order={displayedPanelOrder}
          script={panelScript}
          todayYmd={localTodayYmd()}
          tenantId={displayedPanelOrder.tenantId}
          tenant={displayedPanelOrder.tenant}
          variant={
            fulfillmentGroup === "archive"
              ? "archive"
              : fulfillmentGroup === "history"
                ? "history"
                : "in-production"
          }
          canEdit={canEditOps}
          canEditDue={canEditOps}
          statusOptionMode="ops-admin"
          canViewActivity={canViewActivity}
          canAddComment={canAddComment}
          activityViewer="ops"
          width={panelWidth}
          pendingScriptIds={pendingScriptIds}
          onWidthChange={setPanelWidth}
          onClose={() => selectPanelTarget(null)}
          onTargetChange={selectPanelTarget}
          selectedVideoKey={panelVideoKey}
          onStatusChange={(scriptId, next) =>
            handleStatusChange(scriptId, next)
          }
          onDownloadAsset={(script) =>
            downloadOrderScriptAsset(
              displayedPanelOrder.id,
              script.id,
              displayedPanelOrder.tenantId,
            )
          }
          onViewHistory={() => {
            clearOverviewFields();
            selectTab("history");
          }}
          showViewHistoryAction={ordersTab === "active"}
          showCopyLinkAction={false}
          onDuplicateScript={canDuplicateScripts ? handleDuplicateScript : undefined}
          duplicatePending={duplicateScript.isPending}
          onSendToPartner={
            canSendToPartner
              ? (script) => setSendTarget({ order: displayedPanelOrder, script })
              : undefined
          }
        />
      ) : null}
      {sendTarget ? (
        <SendToPartnerDialog
          orderId={sendTarget.order.id}
          orderScriptId={sendTarget.script.id}
          clientName={sendTarget.order.tenant.name}
          scriptTitle={
            describeOrderScript(
              sendTarget.script,
              t("marketplace.orders.detail.unnamedScript"),
            ).title
          }
          needsTargetRatios={isMissingTargetRatios(sendTarget.order)}
          initialSubmission={unresolvedSends[sendTarget.script.id]}
          pending={sendToPartner.isPending}
          onSubmit={handleSendToPartner}
          onClose={() => setSendTarget(null)}
        />
      ) : null}
    </div>
  );
}
